import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { announcements } from 'announcements/announcements';
import { AnnouncementBanner, AppContent, AppContentLayout, AppLayout, Footer, Header, Nav } from 'components';
import AlertTimeout from 'components/Alert/AlertTimeout';
import MaintenanceModeBanner from 'components/MaintenanceMode/MaintenanceModeBanner';
import { useAppContext } from 'contexts/app';
import { useFeatureFlags } from 'contexts/featureFlags';
import { ErrorScreen } from 'screens';
import { selectCurrentEmployeeRole, selectCurrentEmployeeType } from 'store/Employers';
import { EmployeeRole, FeatureFlags } from 'types';
import { transformCurrentEmployeeRole } from 'utils/employers';
import routeConstants, { URL } from './routeConstants';

interface IProps {
  children: React.ReactNode;
  hideNav?: boolean;
  hideHeader?: boolean;
  hideMakePayment?: boolean;
  requiredRoles?: EmployeeRole[];
}

export const PrivateRouteLayout: React.FC<IProps> = ({ children, hideNav, hideHeader, hideMakePayment, requiredRoles }: IProps) => {
  const appContext = useAppContext();
  const history = useHistory();
  const location = useLocation();
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const maintenanceModeEnabled = useFeatureFlags()[FeatureFlags.MAINTENANCE_MODE]?.enabled;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Dark mode content
  // These set of pages require a dark background
  const darkBackgroundRoutes: string[] = [URL.MakePayment, URL.MakePaymentCalculatorDate, URL.MakePaymentStandardSelectDate, URL.MakePaymentUploadSelectDate];

  if (!appContext.isLoading && requiredRoles && !requiredRoles.includes(transformCurrentEmployeeRole(currentEmployeeType, currentEmployeeRole))) {
    return <ErrorScreen />; // Show 404 if user does not have required access
  }

  // Collect DOB/Pin for the user if not present.
  // Occurs with migration of v1 users or legacy users without a pin.;
  if (appContext.user && history.location.pathname === routeConstants.private.rootPath) {
    if (!appContext.user.dateOfBirth || !appContext.user.hasPin) history.push(routeConstants.private.addDOB);
  }

  return (
    <>
      {!appContext.user?.remoteSession && <AlertTimeout />}
      <AppLayout data-cy="app-layout" navOpen={isNavOpen} hideNav={hideNav} hideHeader={hideHeader}>
        <AppContentLayout>
          {!hideHeader && (
            <Header user={appContext.user} employers={appContext.employers} isNavOpen={isNavOpen} hideNav={hideNav} hideMakePayment={hideMakePayment} onNavOpenChange={() => setIsNavOpen(!isNavOpen)} isLoading={appContext.isLoading} />
          )}
          <Nav dataCy="app-sidebar" isOpen={isNavOpen} isHidden={hideNav} onNavOpenChange={setIsNavOpen} />
          <AppContent darkMode={darkBackgroundRoutes.includes(location.pathname) || location.pathname.includes('/connect')}>
            {maintenanceModeEnabled && <MaintenanceModeBanner />}
            {!appContext.isLoading && location.pathname === routeConstants.private.rootPath && !maintenanceModeEnabled && <AnnouncementBanner announcements={announcements} />}
            {!appContext.isLoading && children}
          </AppContent>
        </AppContentLayout>
        <Footer />
      </AppLayout>
    </>
  );
};
